<script>
/**
 * @component PrescriptionLogPanel
 * @description Panel de auditoría de recetas de medicamentos enviadas a web
 *              services externos. Cada fila es una llamada al motor de recetas
 *              (ej. Quantux): qué se pidió, a qué endpoint se pegó, qué
 *              devolvió, si fue exitosa o no y por qué falló.
 *
 *              Filtros: estado (éxito / error).
 *              Columnas: fecha, paciente, médico, medicamento, motor,
 *                        duración, estado.
 *              Click en fila → modal con el payload enviado, la respuesta
 *                              del web service y el detalle del error.
 *
 *              Modelado sobre WhatsappAuditPanel.
 *
 * @example <PrescriptionLogPanel />
 */
import prescriptionLogService from "@/services/prescription-log";

const PAGE_SIZE = 25;

export default {
  name: "PrescriptionLogPanel",

  data() {
    return {
      loading: false,
      items: [],
      total: 0,
      page: 1,
      pageSize: PAGE_SIZE,

      filters: {
        status: null, // 'success' | 'error' | null (todos)
      },

      detail: {
        visible: false,
        item: null,
      },
    };
  },

  async mounted() {
    await this.load();
  },

  methods: {
    /**
     * @description Carga el log desde el backend con los filtros activos.
     *              La API pagina por `offset`/`limit` (no `page`).
     */
    async load() {
      this.loading = true;
      try {
        const params = {
          offset: (this.page - 1) * this.pageSize,
          limit: this.pageSize,
        };
        if (this.filters.status) params.status = this.filters.status;

        const { data } = await prescriptionLogService.list(params);
        this.items = data.items || [];
        this.total = data.total || 0;
      } catch (e) {
        this.$message.error("No se pudo cargar el log de recetas");
      } finally {
        this.loading = false;
      }
    },

    /**
     * @description Resetea filtros y recarga.
     */
    resetFilters() {
      this.filters = { status: null };
      this.page = 1;
      this.load();
    },

    /**
     * @description Recarga la primera página al cambiar un filtro.
     */
    onFiltersChange() {
      this.page = 1;
      this.load();
    },

    /**
     * @description Cambio de página de la paginación.
     * @param {number} page
     */
    onPageChange(page) {
      this.page = page;
      this.load();
    },

    /**
     * @description Color del tag según el resultado de la llamada.
     * @param {string} status - 'success' | 'error'
     */
    statusTagType(status) {
      if (status === "success") return "success";
      if (status === "error") return "danger";
      return "info";
    },

    /**
     * @description Etiqueta del estado en español.
     * @param {string} status - 'success' | 'error'
     */
    statusLabel(status) {
      if (status === "success") return "Éxito";
      if (status === "error") return "Error";
      return status || "—";
    },

    /**
     * @description Nombre completo de un usuario populado ({ firstName, lastName }).
     * @param {Object} user
     * @returns {string}
     */
    fullName(user) {
      if (!user) return "—";
      return [user.firstName, user.lastName].filter(Boolean).join(" ") || "—";
    },

    /**
     * @description Resumen del medicamento de la receta para la tabla.
     *              Toma el primer item de `request.medications` y, si hay
     *              más de uno, agrega "+N".
     * @param {Object} row
     * @returns {string}
     */
    medSummary(row) {
      const meds =
        row && row.request && Array.isArray(row.request.medications)
          ? row.request.medications
          : [];
      if (!meds.length) return "—";
      const first = meds[0];
      const name =
        first.description ||
        first.brandName ||
        first.drug ||
        first.drugId ||
        "Medicamento";
      return meds.length > 1 ? `${name} +${meds.length - 1}` : name;
    },

    /**
     * @description Formatea la duración de la llamada en ms o s.
     * @param {number} ms
     * @returns {string}
     */
    formatDuration(ms) {
      if (ms == null) return "—";
      if (ms < 1000) return `${ms} ms`;
      return `${(ms / 1000).toFixed(1)} s`;
    },

    /**
     * @description Trunca un texto largo para mostrar en tabla.
     * @param {string} t
     * @param {number} n
     */
    truncate(t, n = 60) {
      if (!t) return "—";
      const s = String(t);
      return s.length > n ? s.substring(0, n) + "…" : s;
    },

    /**
     * @description Formato compacto de fecha — 24hs, TZ forzada a Argentina.
     *              La app es de uso clínico en Argentina, así que se fuerza
     *              `America/Argentina/Buenos_Aires` para evitar desfases si
     *              el browser del cliente está en otra región. Ej: "14/5 17:13".
     * @param {string|Date} d
     */
    formatDate(d) {
      if (!d) return "—";
      return new Date(d)
        .toLocaleString("es-AR", {
          timeZone: "America/Argentina/Buenos_Aires",
          day: "2-digit",
          month: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        })
        .replace(",", "");
    },

    /**
     * @description Serializa un objeto a JSON legible para el modal de detalle.
     * @param {*} obj
     * @returns {string}
     */
    pretty(obj) {
      if (obj == null) return "—";
      try {
        return JSON.stringify(obj, null, 2);
      } catch (e) {
        return String(obj);
      }
    },

    /**
     * @description Abre el modal de detalle. El listado ya trae el registro
     *              completo (request + response), así que no hace falta otra
     *              llamada al backend.
     * @param {Object} row
     */
    openDetail(row) {
      this.detail.item = row;
      this.detail.visible = true;
    },
  },
};
</script>

<template lang="pug">
  .rx-log(v-loading="loading")
    //- El título "Log de recetas" lo pinta el header global (Main.vue).
    .rx-log__actions
      el-button(size="small" type="primary" icon="el-icon-refresh" :loading="loading" @click="load") Actualizar

    .rx-log__filters
      .rx-log__filter
        label.rx-log__filter-label Estado
        el-select(
          v-model="filters.status"
          placeholder="Todos"
          clearable
          @change="onFiltersChange"
          style="width: 160px"
        )
          el-option(label="Éxito" value="success")
          el-option(label="Error" value="error")
      .rx-log__filter
        el-button(size="small" @click="resetFilters") Limpiar

    el-table(
      :data="items"
      stripe
      @row-click="openDetail"
      style="cursor: pointer"
    )
      el-table-column(label="Fecha" width="120")
        template(slot-scope="{ row }") {{ formatDate(row.createdAt) }}

      el-table-column(label="Paciente" min-width="150")
        template(slot-scope="{ row }") {{ fullName(row.patient) }}

      el-table-column(label="Médico" min-width="150")
        template(slot-scope="{ row }") {{ fullName(row.doctor) }}

      el-table-column(label="Medicamento" min-width="220" show-overflow-tooltip)
        template(slot-scope="{ row }") {{ medSummary(row) }}

      el-table-column(label="Motor" width="110")
        template(slot-scope="{ row }")
          el-tag(size="small" type="info") {{ row.engine || '—' }}

      el-table-column(label="Duración" width="100")
        template(slot-scope="{ row }") {{ formatDuration(row.durationMs) }}

      el-table-column(label="Estado" width="130")
        template(slot-scope="{ row }")
          .rx-log__status-cell
            el-tag(:type="statusTagType(row.status)" size="small")
              | {{ statusLabel(row.status) }}
            span.rx-log__http(v-if="row.httpStatus") {{ row.httpStatus }}

    .rx-log__pagination(v-if="total > pageSize")
      el-pagination(
        layout="prev, pager, next, total"
        :total="total"
        :current-page="page"
        :page-size="pageSize"
        @current-change="onPageChange"
      )

    //- ──────── DIALOG DETALLE ────────
    el-dialog(
      title="Detalle de la receta enviada"
      :visible.sync="detail.visible"
      width="820px"
    )
      .rx-detail(v-if="detail.item")
        //- Resumen
        .rx-detail__row
          .rx-detail__col
            .rx-detail__label Fecha
            .rx-detail__value {{ formatDate(detail.item.createdAt) }}
          .rx-detail__col
            .rx-detail__label Estado
            .rx-detail__value
              el-tag(:type="statusTagType(detail.item.status)" size="small")
                | {{ statusLabel(detail.item.status) }}
          .rx-detail__col
            .rx-detail__label Motor / HTTP
            .rx-detail__value
              | {{ detail.item.engine || '—' }}
              span(v-if="detail.item.httpStatus")  · {{ detail.item.httpStatus }}
          .rx-detail__col
            .rx-detail__label Duración
            .rx-detail__value {{ formatDuration(detail.item.durationMs) }}

        .rx-detail__row
          .rx-detail__col
            .rx-detail__label Paciente
            .rx-detail__value {{ fullName(detail.item.patient) }}
          .rx-detail__col
            .rx-detail__label Médico
            .rx-detail__value {{ fullName(detail.item.doctor) }}

        .rx-detail__section
          .rx-detail__label Endpoint externo
          .rx-detail__value.rx-detail__endpoint {{ detail.item.endpoint || '—' }}

        //- Error (si falló)
        .rx-detail__section(v-if="detail.item.status === 'error'")
          .rx-detail__section-title Motivo del error
          .rx-detail__error {{ detail.item.error || 'Sin detalle' }}

        //- Payload enviado
        .rx-detail__section
          .rx-detail__section-title Se pidió (payload enviado)
          pre.rx-detail__pre {{ pretty(detail.item.request) }}

        //- Respuesta del web service
        .rx-detail__section
          .rx-detail__section-title Devolvió el web service
          pre.rx-detail__pre {{ pretty(detail.item.response) }}
</template>

<style lang="scss" scoped>
.rx-log {
  ::v-deep .el-table {
    font-size: 12px;

    th,
    td {
      padding: 6px 8px;
    }
    .cell {
      line-height: 1.4;
    }
  }

  &__actions {
    display: flex;
    justify-content: flex-start;
    gap: 8px;
    margin-bottom: 12px;
  }

  &__filters {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 12px;
    padding: 10px 14px;
    background: #f9fafb;
    border-radius: 8px;
    margin-bottom: 14px;
    font-size: 12px;
  }

  &__filter {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__filter-label {
    font-size: 11px;
    font-weight: 500;
    color: #374151;
  }

  &__status-cell {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
  }

  &__http {
    font-size: 11px;
    color: #6b7280;
  }

  &__pagination {
    display: flex;
    justify-content: center;
    margin-top: 16px;
  }
}

.rx-detail {
  &__row {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 16px;
    margin-bottom: 8px;
  }

  &__col {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__label {
    font-size: 12px;
    font-weight: 500;
    color: #6b7280;
    margin-top: 8px;
  }

  &__value {
    font-size: 14px;
    color: #111827;
  }

  &__endpoint {
    font-family: monospace;
    font-size: 12px;
    word-break: break-all;
  }

  &__section {
    border-top: 1px solid #e5e7eb;
    padding-top: 12px;
    margin-top: 12px;
  }

  &__section-title {
    font-size: 13px;
    font-weight: 600;
    color: #1f2937;
    margin-bottom: 8px;
  }

  &__error {
    background: #fee2e2;
    color: #991b1b;
    padding: 10px 12px;
    border-radius: 6px;
    font-size: 13px;
    line-height: 1.4;
    white-space: pre-wrap;
  }

  &__pre {
    background: #1f2937;
    color: #d1d5db;
    padding: 12px;
    border-radius: 6px;
    font-size: 11px;
    max-height: 280px;
    overflow: auto;
    white-space: pre-wrap;
    word-break: break-all;
  }
}
</style>
